import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';
import {
  createSystemPrompt,
  FunctionTypes,
  SlotUtils,
  UsageLimit,
  ChatMessage,
} from '../types/chatTypes';

class GeminiService {
  private genAI: GoogleGenerativeAI | null = null;
  private model: any = null;
  private deepModel: any = null;
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  // Support triage: guide the user to describe an issue and produce a concise summary
  async generateSupportStream(
    userMessage: string,
    conversationHistory: ChatMessage[],
    onChunk: (chunk: string, fullText: string) => void
  ) {
    if (!this.isInitialized) {
      return {
        success: false,
        message: 'Gemini service unavailable. Please try again later.',
        timestamp: new Date().toISOString(),
      };
    }

    try {
      const systemPrompt = [
        'You are Flashback Support triage assistant. Your job is to:',
        "- Ask brief, targeted follow-up questions to understand the user's problem (environment, steps, expected vs actual, severity).",
        '- Keep responses concise and friendly.',
        '- When you have enough details, output a single concise summary between <summary> and </summary> tags (max ~10 lines).',
        '- The summary should include: title, context, steps to reproduce, expected, actual, impact/severity, user contact (if provided).',
        '- Do not include code fences in the summary; plain text only.',
      ].join('\n');

      const messages = [
        { role: 'user', parts: [{ text: systemPrompt }] },
        ...conversationHistory.map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'model',
          parts: [{ text: msg.content }],
        })),
        { role: 'user', parts: [{ text: userMessage }] },
      ];

      const chat = this.model.startChat({ history: messages.slice(0, -1) });
      const result = await chat.sendMessageStream(userMessage);
      let fullText = '';

      for await (const chunk of result.stream) {
        const chunkText = chunk.text();
        fullText += chunkText;
        if (onChunk) onChunk(chunkText, fullText);
      }

      const hasSummary = /<summary>[\s\S]*<\/summary>/i.test(fullText);

      return {
        success: true,
        message: fullText,
        timestamp: new Date().toISOString(),
        summaryReady: hasSummary,
      } as { success: true; message: string; timestamp: string; summaryReady: boolean };
    } catch (error) {
      return {
        success: false,
        message: "Sorry, I'm experiencing technical difficulties. Please try again.",
        timestamp: new Date().toISOString(),
      };
    }
  }

  async initialize() {
    try {
      // Get API key from environment variables
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

      if (!apiKey) {
        throw new Error('Gemini API key missing. Please configure REACT_APP_GEMINI_API_KEY.');
      }

      this.genAI = new GoogleGenerativeAI(apiKey);

      // Base model configuration optimized for fast conversation
      this.model = this.genAI.getGenerativeModel({
        model: 'gemini-1.5-flash',
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 2048,
        },
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
      });

      // Deep model for more detailed analysis (configurable via env)
      const deepModelName = process.env.REACT_APP_GEMINI_DEEP_MODEL || 'gemini-1.5-pro';
      this.deepModel = this.genAI.getGenerativeModel({
        model: deepModelName,
        generationConfig: {
          temperature: 0.6,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 4096,
        },
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
      });

      this.isInitialized = true;
    } catch (error) {
      this.isInitialized = false;
      throw error;
    }
  }

  // Main method to send message with slot management
  async sendMessageWithSlots(
    userMessage: string,
    currentSlots: Record<string, string>,
    conversationHistory: ChatMessage[],
    expectedSlotKey: string | null,
    options?: { deep?: boolean }
  ) {
    if (!this.isInitialized) {
      return {
        success: false,
        message: 'Gemini service unavailable. Please try again later.',
        timestamp: new Date().toISOString(),
      };
    }

    try {
      // Check usage limit
      if (UsageLimit.hasReachedLimit() && conversationHistory.length > 1) {
        const nextDate = UsageLimit.getRemainingTime();
        return {
          success: true,
          message: `Sorry, you have already received a recommendation this month. You can get a new recommendation starting from ${nextDate?.toLocaleDateString(
            'en-US'
          )}.`,
          timestamp: new Date().toISOString(),
          functionCall: {
            name: FunctionTypes.LIMIT_REACHED,
            parameters: {
              nextAvailableDate: nextDate?.toLocaleDateString('en-US'),
            },
          },
        };
      }

      // Create system prompt
      const systemPrompt = createSystemPrompt(currentSlots, expectedSlotKey, options);

      // Build history for Gemini
      const messages = [
        { role: 'user', parts: [{ text: systemPrompt }] },
        ...conversationHistory.map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'model',
          parts: [{ text: msg.content }],
        })),
        { role: 'user', parts: [{ text: userMessage }] },
      ];

      // Start chat with history
      const activeModel = options?.deep ? this.deepModel : this.model;
      const chat = activeModel.startChat({
        history: messages.slice(0, -1), // Everything except the last message
      });

      // Send user message
      const result = await chat.sendMessage(userMessage);
      const response = await result.response;
      const text = response.text();

      // Detect response type and function call
      const functionCall = this.detectFunctionCall(text, currentSlots, expectedSlotKey);

      return {
        success: true,
        message: text,
        timestamp: new Date().toISOString(),
        functionCall,
      };
    } catch (error) {
      return {
        success: false,
        message: "Sorry, I'm experiencing technical difficulties. Please try again.",
        timestamp: new Date().toISOString(),
      };
    }
  }

  async generateStreamResponseWithSlots(
    userMessage: string,
    currentSlots: Record<string, string>,
    conversationHistory: ChatMessage[],
    expectedSlotKey: string | null,
    onChunk: (chunk: string, fullText: string) => void,
    options?: { deep?: boolean }
  ) {
    if (!this.isInitialized) {
      return {
        success: false,
        message: 'Gemini service unavailable. Please try again later.',
        timestamp: new Date().toISOString(),
      };
    }

    try {
      // Check usage limit
      if (UsageLimit.hasReachedLimit() && conversationHistory.length > 1) {
        const nextDate = UsageLimit.getRemainingTime();
        const message = `Sorry, you have already received a recommendation this month. You can get a new recommendation starting from ${nextDate?.toLocaleDateString(
          'en-US'
        )}.`;

        if (onChunk) {
          onChunk(message, message);
        }

        return {
          success: true,
          message,
          timestamp: new Date().toISOString(),
          functionCall: {
            name: FunctionTypes.LIMIT_REACHED,
            parameters: {
              nextAvailableDate: nextDate?.toLocaleDateString('en-US'),
            },
          },
        };
      }

      // Create system prompt
      const systemPrompt = createSystemPrompt(currentSlots, expectedSlotKey, options);

      // Build history for Gemini
      const messages = [
        { role: 'user', parts: [{ text: systemPrompt }] },
        ...conversationHistory.map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'model',
          parts: [{ text: msg.content }],
        })),
        { role: 'user', parts: [{ text: userMessage }] },
      ];

      // Start chat with history
      let activeModel = options?.deep ? this.deepModel : this.model;
      let chat = activeModel.startChat({
        history: messages.slice(0, -1),
      });

      // Send message with streaming, retry on 503 overload and fallback to base model if deep
      let fullText = '';
      const maxRetries = 2;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const result = await chat.sendMessageStream(userMessage);
          for await (const chunk of result.stream) {
            const chunkText = chunk.text();
            fullText += chunkText;
            if (onChunk) {
              onChunk(chunkText, fullText);
            }
          }
          break; // success
        } catch (err: any) {
          const errMsg = String(err?.message || err);
          const is503 = errMsg.includes('503') || /overloaded/i.test(errMsg);
          if (!is503) throw err;

          // If deep model overloaded, fallback once to base model immediately
          if (options?.deep && activeModel === this.deepModel) {
            activeModel = this.model;
            chat = activeModel.startChat({ history: messages.slice(0, -1) });
            continue;
          }

          if (attempt < maxRetries) {
            const backoffMs = 500 * Math.pow(2, attempt); // 500ms, 1000ms
            await new Promise((res) => setTimeout(res, backoffMs));
            // re-create chat for a clean retry
            chat = activeModel.startChat({ history: messages.slice(0, -1) });
            continue;
          }

          throw err;
        }
      }

      // Detect response type and function call
      const functionCall = this.detectFunctionCall(fullText, currentSlots, expectedSlotKey);

      return {
        success: true,
        message: fullText,
        timestamp: new Date().toISOString(),
        functionCall,
      };
    } catch (error) {
      return {
        success: false,
        message: "Sorry, I'm experiencing technical difficulties. Please try again.",
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Detect function calls in AI response
  private detectFunctionCall(
    responseText: string,
    currentSlots: Record<string, string>,
    expectedSlotKey: string | null
  ) {
    const lowerText = responseText.toLowerCase();

    // Detect ask_for_data (AI asking a question)
    if (
      lowerText.includes('?') &&
      (lowerText.includes('what') ||
        lowerText.includes('which') ||
        lowerText.includes('how') ||
        lowerText.includes('where') ||
        lowerText.includes('when') ||
        lowerText.includes('could you') ||
        lowerText.includes('can you') ||
        lowerText.includes('do you'))
    ) {
      return {
        name: FunctionTypes.ASK_FOR_DATA,
        parameters: {
          slotKey: expectedSlotKey,
          question: responseText,
          context: 'Question asked to collect information',
        },
      };
    }

    // Detect clarify_request (asking for clarification)
    if (
      lowerText.includes('clarif') ||
      lowerText.includes('specific') ||
      lowerText.includes('detail') ||
      lowerText.includes('explain') ||
      lowerText.includes('what do you mean') ||
      lowerText.includes('could you be more')
    ) {
      return {
        name: FunctionTypes.CLARIFY_REQUEST,
        parameters: {
          originalQuestion: expectedSlotKey,
          clarification: responseText,
        },
      };
    }

    // Detect skip_slot (accepting "I don't know")
    if (
      lowerText.includes('no problem') ||
      lowerText.includes("that's okay") ||
      lowerText.includes('we can continue') ||
      lowerText.includes("let's move on") ||
      lowerText.includes('without this information')
    ) {
      return {
        name: FunctionTypes.SKIP_SLOT,
        parameters: {
          slotKey: expectedSlotKey,
          warning: 'Information skipped - this may reduce recommendation accuracy',
        },
      };
    }

    // Detect recommendation (final recommendation)
    if (this.isRecommendationResponse(responseText, currentSlots)) {
      // Record usage
      UsageLimit.recordRecommendation();

      return {
        name: FunctionTypes.RECOMMENDATION,
        parameters: {
          recommendation: responseText,
          confidence: this.calculateConfidence(currentSlots),
        },
      };
    }

    return null; // No function call detected
  }

  // Check if it's a final recommendation
  private isRecommendationResponse(text: string, currentSlots: Record<string, string>) {
    const lowerText = text.toLowerCase();
    const filledSlots = Object.keys(currentSlots).filter((key) => currentSlots[key]);

    // If we have required slots AND text looks like a recommendation
    const hasRequiredSlots = SlotUtils.areRequiredSlotsFilled(currentSlots);
    const looksLikeRecommendation =
      lowerText.includes('recommend') ||
      lowerText.includes('suggest') ||
      lowerText.includes('propose') ||
      lowerText.includes("here's what") ||
      lowerText.includes('based on your') ||
      (lowerText.includes('flashback') && lowerText.includes('solution')) ||
      (text.length > 200 && filledSlots.length >= 2); // Long response with data

    return hasRequiredSlots && looksLikeRecommendation;
  }

  // Calculate recommendation confidence level
  private calculateConfidence(currentSlots: Record<string, string>) {
    const totalSlots = Object.keys(SlotUtils).length;
    const filledSlots = Object.keys(currentSlots).filter((key) => currentSlots[key]).length;

    if (filledSlots === totalSlots) return 'High';
    if (filledSlots >= totalSlots * 0.7) return 'Medium';
    return 'Limited';
  }
}

export default new GeminiService();
